import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { AUTO_INSIGHTS_DRAWER, AUTO_INSIGHTS_SETTINGS } from '@src/utils/sensor/track';
import { track } from '@utils';
import { CDN_ACCOUNT_TYPE } from '@utils/constants';
import {
  aiBrokenAssetsSelector,
  accountTypeSelector,
  aiNewappVersionSelector,
  featureSelector,
} from '@store/common/selectors';
import {
  StyledDrawer,
  StyledCollapse,
  ComingSoonText,
  StyledTag,
  NotificationDot,
  HelpLinkInToolTip,
  StyledHeader,
} from './styled';
import AlertInsights from './AlertInsights';
import BrokenAssets from './BrokenAssets';
import NewAppVersion from './NewAppVersion';
import { SectionExtra } from './Common';
import SettingsDrawer, { SettingTypes } from './Settings';
import useDrawerState from './Hooks/useDrawerState';
import Header from './Header';
import { Collapse, Tooltip, Button } from 'antd';
import { DownloadOutlined, BellOutlined } from '@ant-design/icons';
import { useExportAutoInsight } from './hooks';
import featureNames from '@src/utils/featureToggle/constants';
import { OPEN_EMAIL_SUBSCRIPTIONS_EVENT } from '@src/components/notification-channel/EmailSubscriptions/index';
import HelpIco from '@icons/common/help.svg';
import { AUTO_INSIGHTS_HELP } from '@utils/help/links';
import { OPEN_AUTO_INSIGHTS_DRAWER_EVENT } from './constants';

const { Panel } = Collapse;

export enum ExportCategory {
  NewAppVersion,
  BrokenAsset,
  AIAlertInsights,
}

function AutoInsightsDrawer() {
  const [visible, setVisible] = useState(false);
  const settingsDrawer = useDrawerState({ trackKey: AUTO_INSIGHTS_SETTINGS });
  const exportHandler = useExportAutoInsight();

  const onClose = () => {
    setVisible(false);
  };

  const handleBrokenAssetsSubscribe = (e) => {
    e.stopPropagation();
    const myEvent = new CustomEvent(OPEN_EMAIL_SUBSCRIPTIONS_EVENT);
    window.dispatchEvent(myEvent);
  };

  useEffect(() => {
    document.addEventListener(OPEN_AUTO_INSIGHTS_DRAWER_EVENT, ({ detail }: any) => {
      const { visible } = detail;
      setVisible(visible);
      if (visible) {
        track({ event: AUTO_INSIGHTS_DRAWER });
      }
    });
  }, []);

  const aiAlerts = useSelector(featureSelector(featureNames.AI_ALERTS));
  const aiBrokenAssets = useSelector(aiBrokenAssetsSelector);
  const customerAccountType = useSelector(accountTypeSelector);
  const aiNewappVersion = useSelector(aiNewappVersionSelector);

  return (
    <div>
      <StyledDrawer
        title={<Header openSettings={settingsDrawer.openDrawer} />}
        placement="right"
        closable
        onClose={onClose}
        open={visible}
        key="auto-insights-drawer"
        mask
        width={400}
        destroyOnHidden={true}
      >
        <StyledCollapse
          bordered={false}
          defaultActiveKey={[
            SettingTypes.alertInsights,
            SettingTypes.brokenAssests,
            SettingTypes.appVersionPerf,
          ]}
        >
          {customerAccountType !== CDN_ACCOUNT_TYPE && aiNewappVersion ? (
            <Panel
              header={
                <StyledHeader>
                  <span>New App Version</span>
                  {aiNewappVersion ? (
                    <Tooltip title="Export">
                      <Button
                        type="text"
                        icon={<DownloadOutlined style={{ fontSize: 16 }} />}
                        onClick={(e) => {
                          exportHandler(ExportCategory.NewAppVersion, e);
                        }}
                      />
                    </Tooltip>
                  ) : null}
                </StyledHeader>
              }
              key={SettingTypes.appVersionPerf}
              extra={<SectionExtra text="" />}
            >
              {aiNewappVersion ? (
                <NewAppVersion />
              ) : (
                <ComingSoonText>
                  {
                    'This Product is not included in your company’s product packaging. Contact your Conviva representative for more information.'
                  }
                </ComingSoonText>
              )}
            </Panel>
          ) : (
            // This is a temporary message will be removed while going live
            <Panel
              header={<StyledHeader>{'New App Version'}</StyledHeader>}
              key={SettingTypes.lowCohorts}
              extra={<StyledTag color="cyan">Coming Soon</StyledTag>}
            >
              <ComingSoonText>
                {
                  'Automatic detection of app upgrades and performance comparison to previous stable version.'
                }
              </ComingSoonText>
            </Panel>
          )}
          {customerAccountType !== CDN_ACCOUNT_TYPE ? (
            <Panel
              header={
                <StyledHeader>
                  <span>
                    <span>Broken Assets</span>
                    <Tooltip
                      placement="bottom"
                      title={() => {
                        return (
                          <span>
                            The broken assets run every hour with the last 48 hours of data.
                            <HelpLinkInToolTip
                              href={AUTO_INSIGHTS_HELP}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Learn more
                            </HelpLinkInToolTip>
                          </span>
                        );
                      }}
                    >
                      <HelpIco style={{ marginLeft: '8px', verticalAlign: '-2px' }} />
                    </Tooltip>
                  </span>
                  <span>
                    {aiBrokenAssets ? (
                      <>
                        {
                          <Tooltip title="Subscription">
                            <Button
                              type="text"
                              icon={<BellOutlined style={{ fontSize: 16 }} />}
                              onClick={handleBrokenAssetsSubscribe}
                            >
                              <NotificationDot />
                            </Button>
                          </Tooltip>
                        }
                        <Tooltip title="Export">
                          <Button
                            type="text"
                            icon={<DownloadOutlined style={{ fontSize: 16 }} />}
                            onClick={(e) => {
                              exportHandler(ExportCategory.BrokenAsset, e);
                            }}
                          />
                        </Tooltip>
                      </>
                    ) : null}
                  </span>
                </StyledHeader>
              }
              key={SettingTypes.brokenAssests}
              extra={<SectionExtra text="" />}
            >
              {aiBrokenAssets ? (
                <BrokenAssets />
              ) : (
                <ComingSoonText>
                  {
                    'This Product is not included in your company’s product packaging. Contact your Conviva representative for more information.'
                  }
                </ComingSoonText>
              )}
            </Panel>
          ) : (
            ''
          )}
          <Panel
            header={
              <StyledHeader>
                <span>AI Alert Insights</span>
                {aiAlerts ? (
                  <Tooltip title="Export">
                    <Button
                      type="text"
                      icon={<DownloadOutlined style={{ fontSize: 16 }} />}
                      onClick={(e) => {
                        exportHandler(ExportCategory.AIAlertInsights, e);
                      }}
                    />
                  </Tooltip>
                ) : null}
              </StyledHeader>
            }
            key={SettingTypes.alertInsights}
            extra={<SectionExtra text="" />}
          >
            {aiAlerts ? (
              <AlertInsights />
            ) : (
              <ComingSoonText>
                {' '}
                AI alerts should be enabled to view alert insights. Please contact Conviva support.{' '}
              </ComingSoonText>
            )}
          </Panel>
          <SettingsDrawer
            visible={settingsDrawer.visible}
            closeSettings={settingsDrawer.closeDrawer}
          />
        </StyledCollapse>
      </StyledDrawer>
    </div>
  );
}

export default AutoInsightsDrawer;
